import { useState } from 'react'
import {
    isActiveOrPaused,
    useAffiliateCodes,
    useAffiliateCommissions,
    useAffiliatePayouts,
    useAffiliateReferrals,
    useAffiliateStats,
    useConnectDashboard,
    useRequestPayout,
} from 'src/hooks/useAffiliate'
import styled from 'styled-components'
import { AffiliateMe, Balance, CommissionStatus, ReferralStatus, Stats, StatsRange } from 'src/types/Affiliate'
import { formatBps, formatCents, formatDay } from 'src/utils/affiliateFormat'
import { notify } from 'src/utils/notify'
import ConnectBanner from './ConnectBanner'
import EarningsChart from './EarningsChart'
import {
    COMMISSION_STATUS_OPTIONS,
    CommissionKindBadge,
    CommissionStatusBadge,
    errorMessage,
    payoutBlockedText,
    PayoutStatusBadge,
    REFERRAL_STATUS_OPTIONS,
    ReferralStatusBadge,
} from './labels'
import CodesCard from './CodesCard'
import ShareCard from './ShareCard'
import {
    Badge,
    Banner,
    Card,
    CardTitle,
    EmptyRow,
    ErrorText,
    Grid,
    InlineSpinner,
    LinkButton,
    Muted,
    Pager,
    ActionButton,
    Row,
    Segmented,
    Select,
    Spacer,
    Table,
    TableWrap,
    Tabs,
} from './ui'

const RANGES: { value: StatsRange; label: string }[] = [
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: '1y', label: '1 year' },
    { value: 'all', label: 'All time' },
]

type TabValue = 'referrals' | 'earnings' | 'payouts'
type Unlock = Stats['chart']['maturing'][number]

/* ---------------------------------- Payouts -------------------------------- */

const RANGE_TEXT: Record<StatsRange, string> = {
    '30d': 'in the last 30 days',
    '90d': 'in the last 90 days',
    '1y': 'in the last year',
    all: 'since you joined',
}

const Figure = styled.p<{ $size: number; $negative?: boolean }>`
    font-size: ${({ $size }) => $size}px;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    color: ${({ $negative }) => ($negative ? '#ff8a94' : 'white')};
`

const FigureLabel = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 4px;
`

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`

// Holds the chart's height while stats load so the cards below don't jump
const ChartSlot = styled.div`
    height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
`

// Stands in for a line of text that hasn't loaded yet, keeping its height
const BLANK = '\u00a0'

const PayoutsCard = ({ me, balance, nextUnlock }: { me: AffiliateMe; balance?: Balance; nextUnlock?: Unlock }) => {
    const requestPayout = useRequestPayout()
    const connectDashboard = useConnectDashboard()
    const blockedText = payoutBlockedText(me.payoutBlockedReason ?? null, me.amountToThresholdCents, me.minPayoutCents)
    const available = balance ? balance.availableCents : 0

    const request = () => {
        if (!window.confirm(`Withdraw your full available balance of ${formatCents(available)} to Stripe?`)) return
        requestPayout.mutate(undefined, {
            onSuccess: (payout) => {
                if (payout.status === 'requested') notify('Payout requested. It will be sent once approved.', 'success')
                else if (payout.status === 'processing') notify('Payout is processing. This can take a minute.', 'info')
                else if (payout.status === 'partially_paid')
                    notify(`${formatCents(payout.amountCents)} sent. The rest stays in your balance.`, 'warn')
                else notify(`${formatCents(payout.amountCents)} sent to your Stripe account.`, 'success')
            },
            onError: (error) => notify(errorMessage(error), 'error'),
        })
    }

    const openStripeDashboard = () => {
        // Open the tab synchronously so popup blockers allow it, then point it at the login link.
        const popup = window.open('', '_blank')
        connectDashboard.mutate(undefined, {
            onSuccess: ({ url }) => {
                if (popup) popup.location.href = url
                else window.location.href = url
            },
            onError: (error) => {
                popup?.close()
                notify(errorMessage(error), 'error')
            },
        })
    }

    return (
        <Card>
            <CardTitle>Payouts</CardTitle>
            {me.status === 'paused' && (
                <Banner $tone="warn">
                    <p>
                        Your account is paused. You still earn on customers you've already referred, but new referrals
                        aren't credited and payouts are disabled.
                    </p>
                </Banner>
            )}
            <ConnectBanner me={me} />

            <Row $gap={16}>
                <Stat>
                    <FigureLabel>Ready to withdraw</FigureLabel>
                    <Figure $size={32} $negative={available < 0}>
                        {balance ? formatCents(available) : '—'}
                    </Figure>
                </Stat>
                <Spacer />
                <ActionButton
                    disabled={!me.canRequestPayout || requestPayout.isPending}
                    onClick={request}
                    title={blockedText || undefined}
                >
                    Request payout
                </ActionButton>
            </Row>
            {available < 0 && <Muted>{formatCents(-available)} owed after a refund. Future earnings will cover it.</Muted>}
            {!me.canRequestPayout && blockedText && available >= 0 && <Muted>{blockedText}</Muted>}

            {balance ? (
                <>
                    <Muted>
                        {balance.pendingCents > 0
                            ? `${formatCents(balance.pendingCents)} still unlocking${
                                  nextUnlock
                                      ? `. Next: ${formatCents(nextUnlock.amountCents)} on ${formatDay(nextUnlock.date)}.`
                                      : '.'
                              }`
                            : `Earnings unlock ${me.holdDays} days after each payment.`}
                    </Muted>
                    <Muted>
                        {formatCents(balance.paidCents)} paid out so far
                        {balance.inFlightCents > 0 ? `, ${formatCents(balance.inFlightCents)} on its way.` : '.'}
                        {me.connect?.lastBankPayout?.status === 'paid' &&
                            ` Last bank deposit ${formatDay(me.connect.lastBankPayout.arrivalDate)}.`}
                    </Muted>
                </>
            ) : (
                <>
                    <Muted>{BLANK}</Muted>
                    <Muted>{BLANK}</Muted>
                </>
            )}
            {(me.connect?.status === 'enabled' || me.connect?.status === 'restricted') && (
                <LinkButton onClick={openStripeDashboard} disabled={connectDashboard.isPending}>
                    Open your Stripe dashboard
                </LinkButton>
            )}
        </Card>
    )
}

/* ---------------------------------- Tables --------------------------------- */

const ReferralsTab = ({ enabled }: { enabled: boolean }) => {
    const [page, setPage] = useState(1)
    const [status, setStatus] = useState<ReferralStatus | ''>('')
    const [code, setCode] = useState('')
    const query = useAffiliateReferrals(page, status, code, enabled)
    // The code column and filter only matter once there is more than one code
    const codes = useAffiliateCodes(enabled).data || []
    const showCodes = codes.length > 1
    const colSpan = showCodes ? 6 : 5

    return (
        <>
            <Row>
                <Select
                    aria-label="Filter referrals by status"
                    value={status}
                    onChange={(e) => {
                        setStatus(e.target.value as ReferralStatus | '')
                        setPage(1)
                    }}
                    style={{ width: 'auto' }}
                >
                    <option value="">All referrals</option>
                    {REFERRAL_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </Select>
                {showCodes && (
                    <Select
                        aria-label="Filter referrals by code"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value)
                            setPage(1)
                        }}
                        style={{ width: 'auto' }}
                    >
                        <option value="">All codes</option>
                        {codes.map((c) => (
                            <option key={c.code} value={c.code}>
                                {c.label ? `${c.code} (${c.label})` : c.code}
                            </option>
                        ))}
                    </Select>
                )}
            </Row>
            {query.isError && <ErrorText>{errorMessage(query.error)}</ErrorText>}
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Referral</th>
                            {showCodes && <th>Code</th>}
                            <th>Joined</th>
                            <th>Status</th>
                            <th className="num">Months paid</th>
                            <th className="num">Earned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {query.isPending ? (
                            <EmptyRow colSpan={colSpan}>Loading…</EmptyRow>
                        ) : query.data && query.data.results.length > 0 ? (
                            query.data.results.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.displayName}</td>
                                    {showCodes && <td>{r.code || '—'}</td>}
                                    <td>{formatDay(r.joinedAt)}</td>
                                    <td>
                                        <ReferralStatusBadge status={r.status} />
                                    </td>
                                    <td className="num">{r.monthsPaid}</td>
                                    <td className="num">{formatCents(r.earnedCents)}</td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={colSpan}>
                                {status || code
                                    ? 'No referrals match these filters.'
                                    : 'No referrals yet. Share your link to get started.'}
                            </EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {query.data && <Pager page={page} totalPages={query.data.totalPages} onChange={setPage} />}
        </>
    )
}

const EarningsTab = ({ enabled }: { enabled: boolean }) => {
    const [page, setPage] = useState(1)
    const [status, setStatus] = useState<CommissionStatus | ''>('')
    const query = useAffiliateCommissions(page, status, enabled)

    return (
        <>
            <Row>
                <Select
                    aria-label="Filter earnings by status"
                    value={status}
                    onChange={(e) => {
                        setStatus(e.target.value as CommissionStatus | '')
                        setPage(1)
                    }}
                    style={{ width: 'auto' }}
                >
                    <option value="">All earnings</option>
                    {COMMISSION_STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </Select>
            </Row>
            {query.isError && <ErrorText>{errorMessage(query.error)}</ErrorText>}
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Referral</th>
                            <th className="num">Base</th>
                            <th className="num">Rate</th>
                            <th className="num">Amount</th>
                            <th>Status</th>
                            <th>Available</th>
                        </tr>
                    </thead>
                    <tbody>
                        {query.isPending ? (
                            <EmptyRow colSpan={8}>Loading…</EmptyRow>
                        ) : query.data && query.data.results.length > 0 ? (
                            query.data.results.map((c) => (
                                <tr key={c.id}>
                                    <td>{formatDay(c.createdAt)}</td>
                                    <td>
                                        <CommissionKindBadge kind={c.kind} />
                                        {c.note && <Muted style={{ fontSize: 12, marginTop: 2 }}>{c.note}</Muted>}
                                    </td>
                                    <td>{c.referral ? c.referral.displayName : '—'}</td>
                                    <td className="num">{c.baseCents == null ? '—' : formatCents(c.baseCents)}</td>
                                    <td className="num">{formatBps(c.rateBps)}</td>
                                    <td className="num" style={{ color: c.amountCents < 0 ? '#ff8a94' : undefined }}>
                                        {formatCents(c.amountCents)}
                                    </td>
                                    <td>
                                        <Row $gap={6}>
                                            <CommissionStatusBadge status={c.status} />
                                            {c.underReview && <Badge $tone="yellow">Under review</Badge>}
                                        </Row>
                                    </td>
                                    <td>{c.status === 'pending' ? formatDay(c.availableAt) : '—'}</td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={8}>No earnings yet.</EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {query.data && <Pager page={page} totalPages={query.data.totalPages} onChange={setPage} />}
        </>
    )
}

const PayoutsTab = ({ enabled }: { enabled: boolean }) => {
    const [page, setPage] = useState(1)
    const query = useAffiliatePayouts(page, enabled)

    return (
        <>
            {query.isError && <ErrorText>{errorMessage(query.error)}</ErrorText>}
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Requested</th>
                            <th className="num">Amount</th>
                            <th>Status</th>
                            <th>Paid</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {query.isPending ? (
                            <EmptyRow colSpan={5}>Loading…</EmptyRow>
                        ) : query.data && query.data.results.length > 0 ? (
                            query.data.results.map((p) => (
                                <tr key={p.id}>
                                    <td>{formatDay(p.createdAt)}</td>
                                    <td className="num">{formatCents(p.amountCents)}</td>
                                    <td>
                                        <PayoutStatusBadge status={p.status} />
                                    </td>
                                    <td>{formatDay(p.paidAt)}</td>
                                    <td>{p.failureReason || ''}</td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={5}>No payouts yet.</EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {query.data && <Pager page={page} totalPages={query.data.totalPages} onChange={setPage} />}
        </>
    )
}

/* --------------------------------- Dashboard ------------------------------- */

const AffiliateDashboard = ({ me }: { me: AffiliateMe }) => {
    const enabled = isActiveOrPaused(me)
    const [range, setRange] = useState<StatsRange>('90d')
    const [showTable, setShowTable] = useState(false)
    const [tab, setTab] = useState<TabValue>('referrals')
    const stats = useAffiliateStats(range, enabled)

    const data = stats.data
    const earnedInRange = data ? data.chart.history.reduce((sum, h) => sum + h.earnedCents, 0) : 0

    return (
        <>
            <Card>
                <Row $gap={32}>
                    <Stat>
                        <FigureLabel>Total earned</FigureLabel>
                        <Figure $size={44}>{data ? formatCents(data.balance.lifetimeCents) : '—'}</Figure>
                        <Muted>{data ? `${formatCents(earnedInRange)} ${RANGE_TEXT[range]}` : BLANK}</Muted>
                    </Stat>
                    <Stat>
                        <FigureLabel>Active subscribers</FigureLabel>
                        <Figure $size={44}>{data ? data.referrals.active.toLocaleString() : '—'}</Figure>
                        <Muted>{data ? 'referred by you' : BLANK}</Muted>
                    </Stat>
                    <Spacer />
                    <Segmented options={RANGES} value={range} onChange={setRange} />
                </Row>
                {stats.isError && <ErrorText>{errorMessage(stats.error)}</ErrorText>}
                {data ? (
                    <EarningsChart chart={data.chart} showTable={showTable} />
                ) : (
                    <ChartSlot>{stats.isPending && <InlineSpinner />}</ChartSlot>
                )}
                <Row style={{ justifyContent: 'flex-end', visibility: data ? 'visible' : 'hidden' }}>
                    <LinkButton onClick={() => setShowTable((v) => !v)} disabled={!data}>
                        {showTable ? 'Show chart' : 'Show as table'}
                    </LinkButton>
                </Row>
            </Card>

            <Grid $min={320}>
                <PayoutsCard me={me} balance={data?.balance} nextUnlock={data?.chart.maturing[0]} />
                <ShareCard me={me} />
            </Grid>

            <CodesCard me={me} codes={data?.codes || []} loading={!data} rangeText={RANGE_TEXT[range]} />

            <Card>
                <Tabs
                    tabs={[
                        { value: 'referrals', label: 'Referrals' },
                        { value: 'earnings', label: 'Earnings' },
                        { value: 'payouts', label: 'Payouts' },
                    ]}
                    value={tab}
                    onChange={setTab}
                />
                {tab === 'referrals' && <ReferralsTab enabled={enabled} />}
                {tab === 'earnings' && <EarningsTab enabled={enabled} />}
                {tab === 'payouts' && <PayoutsTab enabled={enabled} />}
            </Card>
        </>
    )
}

export default AffiliateDashboard
